/**
 * The panel half of axis overview mode.
 *
 * The 3D overlay answers "which way does each servo turn"; this answers
 * "and do the pairs agree", which is a question about a list. It groups
 * the joints by the world direction they turn about -- a leg whose servos
 * line up reads as one block -- and reports the left/right audit, so a
 * single knee mounted the wrong way round shows up as a row rather than
 * as something you have to spot by eye in the viewport.
 */

import { useEffect, useMemo } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Checkbox,
  Group,
  SegmentedControl,
  Slider,
  Stack,
  Switch,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconAlertTriangle, IconArrowsExchange } from "@tabler/icons-react";

import {
  useJointAxes,
  useJoints,
  useJointAxesLabelMode,
  useJointAxesMirrorAxis,
  useJointAxesMode,
  useJointAxesShowFrames,
  useJointAxesSizePx,
  useViewerStore,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { auditJointAxes, axisDirection, type AxisDirection, type Vec3 } from "../../scene/jointAxisAudit";
import { shortJointName } from "../../scene/jointLabels";
import type { JointAxisData } from "../../types";

const DIRECTION_ORDER: AxisDirection[] = ["+X", "-X", "+Y", "-Y", "+Z", "-Z", "oblique"];

const DIRECTION_COLOURS: Record<AxisDirection, string> = {
  "+X": "red",
  "-X": "red",
  "+Y": "green",
  "-Y": "green",
  "+Z": "blue",
  "-Z": "blue",
  oblique: "grape",
};

const RELATION_LABELS = {
  mirror: "mirrored",
  parallel: "same direction",
  skew: "misaligned",
} as const;

/** Drop the instance prefix; the panel is tight and the prefix repeats. */
function jointLabel(joint: JointAxisData): string {
  return shortJointName(joint.name, joint.display_name);
}

function JointRow({
  joint,
  reversed,
  onPick,
  onReverse,
  onHover,
}: {
  joint: JointAxisData;
  reversed: boolean;
  onPick: (name: string) => void;
  onReverse: (name: string, reversed: boolean) => void;
  onHover: (name: string | null) => void;
}) {
  return (
    <Group
      gap={4}
      wrap="nowrap"
      justify="space-between"
      // Running down the list rings each axis in turn and names it, which
      // is how a name too long for a floating label gets read.
      onMouseEnter={() => onHover(joint.name)}
      onMouseLeave={() => onHover(null)}
    >
      <UnstyledButton onClick={() => onPick(joint.name)} style={{ flex: 1, minWidth: 0 }} title={joint.name}>
        <Text size="xs" c="dimmed" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {jointLabel(joint)}
          {reversed && " ⇄"}
        </Text>
      </UnstyledButton>
      {/* The flip belongs next to the direction it disagrees with: this list
          is where a servo mounted the wrong way round shows itself. */}
      <ActionIcon
        size="xs"
        variant={reversed ? "filled" : "subtle"}
        color="orange"
        aria-label={`Reverse ${joint.name}`}
        title={
          reversed
            ? "Restore this joint's own rotation direction"
            : "Reverse this joint's rotation direction (axis negated, limits and servo mapping swapped)"
        }
        onClick={() => onReverse(joint.name, !reversed)}
      >
        <IconArrowsExchange size={12} />
      </ActionIcon>
    </Group>
  );
}

export function JointAxesSection() {
  const mode = useJointAxesMode();
  const axes = useJointAxes();
  const labelMode = useJointAxesLabelMode();
  const showFrames = useJointAxesShowFrames();
  const sizePx = useJointAxesSizePx();
  const mirrorAxis = useJointAxesMirrorAxis();
  const setJointAxesMode = useViewerStore((state) => state.setJointAxesMode);
  const setJointAxesLabelMode = useViewerStore((state) => state.setJointAxesLabelMode);
  const setJointAxesShowFrames = useViewerStore((state) => state.setJointAxesShowFrames);
  const setJointAxesSizePx = useViewerStore((state) => state.setJointAxesSizePx);
  const setJointAxesMirrorAxis = useViewerStore((state) => state.setJointAxesMirrorAxis);
  const setJointAxesHoveredName = useViewerStore((state) => state.setJointAxesHoveredName);
  const jointList = useJoints();
  const { sendEnableJointAxes, sendHighlightJoint, sendSetJointAxisReversed } = useWebSocket();

  const audit = useMemo(() => {
    const mirrorNormal: Vec3 = mirrorAxis === "x" ? [1, 0, 0] : [0, 1, 0];
    return auditJointAxes(axes, { mirrorNormal });
  }, [axes, mirrorAxis]);

  const byDirection = useMemo(() => {
    const groups = new Map<AxisDirection, JointAxisData[]>();
    for (const joint of axes) {
      const direction = axisDirection(joint.axis);
      const existing = groups.get(direction);
      if (existing) {
        existing.push(joint);
      } else {
        groups.set(direction, [joint]);
      }
    }
    return DIRECTION_ORDER.filter((direction) => groups.has(direction)).map((direction) => ({
      direction,
      joints: groups.get(direction) ?? [],
    }));
  }, [axes]);

  // The reversed flag rides with the joint list, not with the axes, so the
  // two are matched up by the name they share.
  const reversedNames = useMemo(
    () => new Set(jointList.filter((joint) => joint.settings?.axis_reversed).map((joint) => joint.name)),
    [jointList]
  );

  const handleToggle = (enabled: boolean) => {
    setJointAxesMode(enabled);
    sendEnableJointAxes(enabled);
  };

  useEffect(() => () => setJointAxesHoveredName(null), [setJointAxesHoveredName]);

  const mirrorCount = audit.pairs.filter((pair) => pair.relation === "mirror").length;
  const parallelCount = audit.pairs.filter((pair) => pair.relation === "parallel").length;

  return (
    <Stack gap="xs">
      <Switch
        checked={mode}
        onChange={(event) => handleToggle(event.currentTarget.checked)}
        label="Joint axis overview"
        description="Every joint's rotation axis, in world coordinates"
        size="sm"
      />

      {mode && (
        <>
          <Checkbox
            size="xs"
            checked={showFrames}
            onChange={(event) => setJointAxesShowFrames(event.currentTarget.checked)}
            label="XYZ frames"
          />

          <Box>
            <Text size="xs" c="dimmed">
              Names
            </Text>
            {/* A robot's worth of names is a wall of text, so the default
                writes only the ones worth reading: the pairs that disagree,
                and whichever joint the pointer is on in the list below. */}
            <SegmentedControl
              size="xs"
              fullWidth
              value={labelMode}
              onChange={(value) =>
                setJointAxesLabelMode(value === "off" || value === "all" ? value : "key")
              }
              data={[
                { value: "key", label: "Flagged" },
                { value: "all", label: "All" },
                { value: "off", label: "Off" },
              ]}
            />
          </Box>

          <Box>
            <Text size="xs" c="dimmed">
              Axis size
            </Text>
            {/* Pixels, not metres: the gizmo is sized against the screen so
                it survives being zoomed in and out of. */}
            <Slider
              size="xs"
              min={16}
              max={140}
              step={2}
              value={sizePx}
              onChange={setJointAxesSizePx}
              label={(value) => `${value} px`}
            />
          </Box>

          <Box>
            <Text size="xs" c="dimmed">
              Left–right axis
            </Text>
            <SegmentedControl
              size="xs"
              fullWidth
              value={mirrorAxis}
              onChange={(value) => setJointAxesMirrorAxis(value === "x" ? "x" : "y")}
              data={[
                { value: "y", label: "Y (robot faces +X)" },
                { value: "x", label: "X (robot faces +Y)" },
              ]}
            />
          </Box>

          {axes.length === 0 ? (
            <Text size="xs" c="dimmed">
              No movable joints in the assembly yet.
            </Text>
          ) : (
            <>
              {audit.skewed.length > 0 && (
                <Alert
                  color="orange"
                  variant="light"
                  icon={<IconAlertTriangle size={16} />}
                  title={`${audit.skewed.length} pair(s) out of line`}
                  p="xs"
                >
                  <Stack gap={2}>
                    {audit.skewed.map((pair) => (
                      <Text key={`${pair.left.name}|${pair.right.name}`} size="xs">
                        {jointLabel(pair.left)} ↔ {jointLabel(pair.right)}:{" "}
                        {pair.deviationDeg.toFixed(1)}° off
                      </Text>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Box>
                <Text size="xs" fw={600}>
                  Left / right pairs
                </Text>
                {audit.pairs.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    No pairs found. Pairing is geometric, so a pose that is not
                    itself symmetric hides them — zero the joints and check again.
                  </Text>
                ) : (
                  <Stack gap={2} mt={4}>
                    {audit.pairs.map((pair) => (
                      <Group key={`${pair.left.name}|${pair.right.name}`} gap={6} wrap="nowrap">
                        <Badge
                          size="xs"
                          variant="light"
                          color={pair.relation === "skew" ? "orange" : pair.relation === "mirror" ? "teal" : "blue"}
                        >
                          {RELATION_LABELS[pair.relation]}
                        </Badge>
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={`${pair.left.name} ↔ ${pair.right.name}`}
                        >
                          {jointLabel(pair.left)} ↔ {jointLabel(pair.right)}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Box>

              {audit.mixedConventions && (
                <Text size="xs" c="dimmed">
                  {mirrorCount} pair(s) mirrored, {parallelCount} mounted the same way
                  round. Both are valid — worth a look if only one pair differs.
                </Text>
              )}

              {audit.unpaired.length > 0 && (
                <Text size="xs" c="dimmed">
                  {audit.unpaired.length} joint(s) with no partner across the plane.
                </Text>
              )}

              {audit.center.length > 0 && (
                <Text size="xs" c="dimmed">
                  {audit.center.length} joint(s) on the mirror plane
                  {audit.center.some((entry) => !entry.symmetric)
                    ? ", some of which break left/right symmetry when moved (a waist yaw does this by design)."
                    : "."}
                </Text>
              )}

              <Box>
                <Text size="xs" fw={600}>
                  Axis directions
                </Text>
                <Stack gap={4} mt={4}>
                  {byDirection.map(({ direction, joints }) => (
                    <Box key={direction}>
                      <Group gap={6}>
                        <Badge size="xs" variant="light" color={DIRECTION_COLOURS[direction]}>
                          {direction === "oblique" ? "off-axis" : direction}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {joints.length}
                        </Text>
                      </Group>
                      <Box pl="sm">
                        {joints.map((joint) => (
                          <JointRow
                            key={joint.name}
                            joint={joint}
                            reversed={reversedNames.has(joint.name)}
                            onPick={sendHighlightJoint}
                            onReverse={sendSetJointAxisReversed}
                            onHover={setJointAxesHoveredName}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
